/**
 * Journée COMMERCIALE — l'unité de temps de toute la caisse.
 *
 * Une journée commerciale n'est pas forcément une journée civile : un café qui
 * ferme à 2 h du matin veut que ses ventes de 1 h 30 comptent pour la veille.
 * Le décalage est porté par `dayCutoffMinutes` (minutes après minuit) :
 *
 *   cutoff = 0   → journée civile         [00:00, 24:00)
 *   cutoff = 240 → journée « café »       [04:00, 04:00 lendemain)
 *
 * Toute la couche statistique raisonne en clés `YYYY-MM-DD`, jamais en
 * instants : c'est ce qui rend les comparaisons jour/mois/année insensibles au
 * fuseau et aux heures d'été. La conversion instant → journée se fait ICI et
 * nulle part ailleurs.
 *
 * Arithmétique de CALENDRIER volontaire (pas de décalage en millisecondes) :
 * un décalage en ms se trompe d'une heure lors d'un changement d'heure d'été.
 * La Tunisie n'en pratique pas, mais le produit est extensible.
 */

const MINUTES_PER_DAY = 1440;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function toDate(instant: Date | string): Date {
  return instant instanceof Date ? instant : new Date(instant);
}

/** Clé `YYYY-MM-DD` de la date CIVILE locale d'un Date. */
function localDayKey(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function normalizeCutoff(cutoffMinutes: number): number {
  if (!Number.isFinite(cutoffMinutes)) return 0;
  const n = Math.trunc(cutoffMinutes);
  if (n < 0) return 0;
  if (n >= MINUTES_PER_DAY) return 0;
  return n;
}

/** `true` si la chaîne est une clé de journée commerciale valide. */
export function isBusinessDay(day: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return false;
  const parts = day.split('-').map(Number);
  const y = parts[0] as number;
  const m = parts[1] as number;
  const d = parts[2] as number;
  const probe = new Date(y, m - 1, d);
  return (
    probe.getFullYear() === y && probe.getMonth() === m - 1 && probe.getDate() === d
  );
}

/** Décompose une clé de journée en composantes numériques. */
export function businessDayParts(day: string): { year: number; month: number; day: number } {
  const parts = day.split('-').map(Number);
  return {
    year: (parts[0] as number) ?? 1970,
    month: (parts[1] as number) ?? 1,
    day: (parts[2] as number) ?? 1,
  };
}

/**
 * Journée commerciale à laquelle appartient un instant.
 * Un instant AVANT la bascule est rattaché à la journée précédente.
 */
export function resolveBusinessDay(
  instant: Date | string,
  cutoffMinutes = 0,
): string {
  const d = toDate(instant);
  if (Number.isNaN(d.getTime())) return '';
  const cutoff = normalizeCutoff(cutoffMinutes);
  const minutesOfDay = d.getHours() * 60 + d.getMinutes();
  // Copie sur la date civile locale, puis recul d'un jour si on est encore
  // « avant l'ouverture » — arithmétique de calendrier, pas de millisecondes.
  const base = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (minutesOfDay < cutoff) base.setDate(base.getDate() - 1);
  return localDayKey(base);
}

/** Journée commerciale en cours. */
export function currentBusinessDay(cutoffMinutes = 0, now: Date = new Date()): string {
  return resolveBusinessDay(now, cutoffMinutes);
}

/**
 * Fenêtre d'instants `[from, to)` couverte par une journée commerciale.
 * Réciproque exacte de `resolveBusinessDay` : pour tout instant t,
 * `businessDayRange(resolveBusinessDay(t, c), c)` contient t.
 */
export function businessDayRange(
  day: string,
  cutoffMinutes = 0,
): { from: string; to: string } {
  const cutoff = normalizeCutoff(cutoffMinutes);
  const { year, month, day: d } = businessDayParts(day);
  // `new Date(y, m, d, 0, minutes)` reporte correctement les minutes > 59.
  const from = new Date(year, month - 1, d, 0, cutoff, 0, 0);
  const to = new Date(year, month - 1, d + 1, 0, cutoff, 0, 0);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** Fenêtre d'instants couvrant un intervalle INCLUSIF de journées. */
export function businessDaySpanRange(
  fromDay: string,
  toDay: string,
  cutoffMinutes = 0,
): { from: string; to: string } {
  return {
    from: businessDayRange(fromDay, cutoffMinutes).from,
    to: businessDayRange(toDay, cutoffMinutes).to,
  };
}

/** Décale une clé de journée de `n` jours (n négatif = passé). */
export function addBusinessDays(day: string, n: number): string {
  const { year, month, day: d } = businessDayParts(day);
  const shifted = new Date(year, month - 1, d + n);
  return localDayKey(shifted);
}

/** Décale une clé de journée de `n` mois, en bornant au dernier jour du mois. */
export function addBusinessMonths(day: string, n: number): string {
  const { year, month, day: d } = businessDayParts(day);
  const target = new Date(year, month - 1 + n, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d, lastDay));
  return localDayKey(target);
}

/** Nombre de journées entières entre deux clés (b − a). */
export function businessDayDiff(a: string, b: string): number {
  const pa = businessDayParts(a);
  const pb = businessDayParts(b);
  const da = Date.UTC(pa.year, pa.month - 1, pa.day);
  const db = Date.UTC(pb.year, pb.month - 1, pb.day);
  return Math.round((db - da) / 86_400_000);
}

/** Comparateur de clés de journée (l'ordre lexicographique suffit, mais on
 *  passe par une fonction nommée pour que l'intention soit lisible). */
export function compareBusinessDays(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Toutes les journées de `from` à `to` INCLUS, ordre croissant. */
export function eachBusinessDay(from: string, to: string): string[] {
  if (!isBusinessDay(from) || !isBusinessDay(to)) return [];
  const span = businessDayDiff(from, to);
  if (span < 0) return [];
  // Garde-fou : une plage aberrante (import corrompu, saisie manuelle) ne doit
  // pas faire exploser la mémoire de l'onglet.
  const count = Math.min(span, 366 * 10);
  const days: string[] = [];
  for (let i = 0; i <= count; i += 1) days.push(addBusinessDays(from, i));
  return days;
}

/** Clé de mois `YYYY-MM` d'une journée. */
export function businessMonthKey(day: string): string {
  return day.slice(0, 7);
}

/** Clé d'année `YYYY` d'une journée. */
export function businessYearKey(day: string): string {
  return day.slice(0, 4);
}
